
import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import { Upload, Trash, Eye } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/service/api-service';

interface FileUploadProps {
  label: string;
  value?: string | null;
  onChange: (value: string | null) => void;
  accept?: string;
  id?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  label,
  value,
  onChange,
  accept = "image/*,application/pdf",
  id = "file-upload",
}) => {
  const [fileName, setFileName] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const progressRef = useRef<number>(0);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setFileName(file.name);
      setIsUploading(true);
      setUploadProgress(0);
      progressRef.current = 0;

      try {
        const response = await apiService.sendDocument(file, "/assets", {
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            progressRef.current = percentCompleted;
            setUploadProgress(percentCompleted);
          }
        });

        // Ensure progress shows 100% when complete
        progressRef.current = 100;
        setUploadProgress(100);

        // Get the UUID from the response
        const uuid = response.data.uuid;
        onChange(uuid);

        toast({
          title: "Upload successful",
          description: "File has been uploaded successfully",
        });
      } catch (error) {
        progressRef.current = 0;
        setUploadProgress(0);
        setFileName(null);

        toast({
          title: "Upload failed",
          description: "There was an error uploading your file",
          variant: "destructive"
        });
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleDelete = async () => {
    if (!value) return;

    try {
      await apiService.delete(`/assets/${value}`);

      onChange(null);
      setFileName(null);

      toast({
        title: "File deleted",
        description: "File has been removed successfully",
      });
    } catch (error) {
      toast({
        title: "Delete failed",
        description: "There was an error removing your file",
        variant: "destructive"
      });
    }
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
  };

  const isPdf = fileName?.toLowerCase().endsWith('.pdf');

  const renderPreview = () => {
    if (!value) return null;

    if (isPdf) {
      return (
        <iframe
          src={`${import.meta.env.VITE_BASE_URL}/assets/${value}`}
          width="100%"
          height="600px"
          title="PDF Preview"
          style={{ border: 'none' }}
        />
      );
    }

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
        <img
          src={`${import.meta.env.VITE_BASE_URL}/assets/${value}`}
          alt="Preview"
          style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="body2" sx={{ mb: 1 }}>{label}</Typography>
      <Box
        sx={{
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          position: 'relative'
        }}
      >
        {value && fileName ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {fileName}
            </Typography>
            <Box>
              <IconButton onClick={() => setPreviewOpen(true)} size="small" sx={{ mr: 1 }}>
                <Eye size={18} />
              </IconButton>
              <IconButton onClick={handleDelete} color="error" size="small">
                <Trash size={18} />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <>
            <input
              type="file"
              accept={accept}
              id={id}
              style={{ display: 'none' }}
              onChange={handleFileChange}
              ref={fileInputRef}
            />
            <label htmlFor={id}>
              <Button
                variant="outlined"
                component="span"
                startIcon={<Upload />}
                disabled={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload File'}
              </Button>
            </label>
          </>
        )}

        {isUploading && (
          <Box sx={{ mt: 2, width: '100%' }}>
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <CircularProgress
                variant="determinate"
                value={uploadProgress}
                size={24}
                sx={{ mr: 1 }}
              />
              <Typography variant="body2">{uploadProgress}%</Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* MUI Dialog for Preview */}
      <Dialog
        open={previewOpen}
        onClose={handlePreviewClose}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: isPdf ? '800px' : '1000px',
            m: 2
          }
        }}
      >
        <DialogTitle>
          File Preview
        </DialogTitle>
        <DialogContent dividers>
          {renderPreview()}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
